import { useToast } from 'bootstrap-vue-next';
import { h, onMounted, onUnmounted } from 'vue';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_URL = '/build/version.json';

export function useVersionCheck() {
    const { create } = useToast();

    let currentVersion: string | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let toastShown = false;

    async function fetchVersion(): Promise<string | null> {
        try {
            const response = await fetch(`${VERSION_URL}?t=${Date.now()}`);
            if (!response.ok) {
                return null;
            }
            // SAFETY: the manifest at VERSION_URL is written by our own build step,
            // which puts the deployed version in it.
            const data = (await response.json()) as { version: string };
            return data.version ?? null;
        } catch {
            return null;
        }
    }

    async function checkVersion(): Promise<void> {
        const latestVersion = await fetchVersion();
        if (!latestVersion) {
            return;
        }

        if (currentVersion === null) {
            currentVersion = latestVersion;
            return;
        }

        if (latestVersion !== currentVersion && !toastShown) {
            toastShown = true;
            showUpdateToast();
        }
    }

    function showUpdateToast(): void {
        create({
            title: 'Update Available',
            noCloseButton: true,
            position: 'bottom-start',
            modelValue: 9999999,
            variant: 'info',
            slots: {
                default: () =>
                    h('div', { class: 'd-flex align-items-center justify-content-between' }, [
                        h('span', 'A new version is available.'),
                        h(
                            'button',
                            {
                                class: 'btn btn-sm btn-primary ms-3',
                                onClick: () => window.location.reload(),
                            },
                            'Refresh',
                        ),
                    ]),
            },
        });
    }

    function onVisibilityChange(): void {
        if (document.visibilityState === 'visible') {
            checkVersion();
        }
    }

    onMounted(() => {
        checkVersion();
        intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', onVisibilityChange);
    });

    onUnmounted(() => {
        if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
    });
}
